// PFMEA Coaching Tips Utility
// ============================================================
// Static, rule-based coaching tips - NO LLM TOKENS USED
// Tips are pre-built based on AIAG-VDA requirements and best practices
// LLM tokens only used when user explicitly asks Magnus in Prime mode
// ============================================================

import { PfmeaItem } from '../types';

// ============================================================
// TIP TYPES & INTERFACES
// ============================================================

export type TipSeverity = 'info' | 'warning' | 'error' | 'success' | 'best-practice';
export type TipCategory =
    | 'validation'
    | 'best-practice'
    | 'aiag-vda'
    | 'coaching'
    | 'sxo-guidance'
    | 'pre-submission';

export interface CoachingTip {
    id: string;
    category: TipCategory;
    severity: TipSeverity;
    title: string;
    message: string;
    field?: string;              // Which field triggered this tip
    aiagVdaRef?: string;         // Reference to AIAG-VDA manual section
    actionLabel?: string;        // Label for action button
    actionPath?: string;         // Path to navigate to
}

export interface ValidationResult {
    isValid: boolean;
    errors: CoachingTip[];
    warnings: CoachingTip[];
    suggestions: CoachingTip[];
}

// ============================================================
// USER EXPERIENCE LEVELS
// ============================================================

export type UserExperienceLevel = 'beginner' | 'intermediate' | 'expert';

export interface UserPreferences {
    experienceLevel: UserExperienceLevel;
    showCoachingTips: boolean;
    showValidationWarnings: boolean;
    showBestPractices: boolean;
}

const DEFAULT_PREFERENCES: UserPreferences = {
    experienceLevel: 'beginner',
    showCoachingTips: true,
    showValidationWarnings: true,
    showBestPractices: true,
};

// ============================================================
// STATIC S×O GUIDANCE MATRIX
// Based on AIAG-VDA Action Priority Logic
// ============================================================

const SxO_GUIDANCE: Record<string, { risk: string; guidance: string; actions: string[] }> = {
    // High Severity (9-10) combinations
    '10-10': { risk: 'CRITICAL', guidance: 'Safety/regulatory failure with high occurrence. IMMEDIATE action required.', actions: ['Add error-proofing (Poka-Yoke)', 'Implement 100% automated inspection', 'Consider design change to eliminate failure mode'] },
    '10-9': { risk: 'CRITICAL', guidance: 'Safety issue with frequent occurrence. High priority for prevention.', actions: ['Add prevention controls at source', 'Implement automated verification', 'Review process capability'] },
    '10-8': { risk: 'CRITICAL', guidance: 'Safety issue with moderate-high occurrence.', actions: ['Add redundant prevention controls', 'Implement in-process inspection'] },
    '10-7': { risk: 'HIGH', guidance: 'Safety issue with moderate occurrence.', actions: ['Strengthen prevention controls', 'Add detection at operation'] },
    '10-6': { risk: 'HIGH', guidance: 'Safety issue with low-moderate occurrence.', actions: ['Verify prevention controls are effective', 'Consider additional detection'] },
    '10-5': { risk: 'HIGH', guidance: 'Safety issue but controlled occurrence.', actions: ['Maintain prevention controls', 'Ensure detection capability'] },
    '10-4': { risk: 'MEDIUM', guidance: 'Safety issue with low occurrence.', actions: ['Monitor prevention effectiveness', 'Maintain detection'] },
    '10-3': { risk: 'MEDIUM', guidance: 'Safety issue with very low occurrence.', actions: ['Continue monitoring', 'Periodic control review'] },
    '10-2': { risk: 'LOW', guidance: 'Safety issue but rare occurrence.', actions: ['Maintain current controls'] },
    '10-1': { risk: 'LOW', guidance: 'Safety issue but very rare.', actions: ['Document controls'] },

    '9-10': { risk: 'CRITICAL', guidance: 'Major failure with very high occurrence.', actions: ['Process redesign needed', 'Add multiple prevention layers'] },
    '9-9': { risk: 'CRITICAL', guidance: 'Major failure with high occurrence.', actions: ['Implement error-proofing', 'Add automated inspection'] },
    '9-8': { risk: 'HIGH', guidance: 'Major failure with moderate-high occurrence.', actions: ['Strengthen prevention controls', 'Add verification step'] },
    '9-7': { risk: 'HIGH', guidance: 'Major failure with moderate occurrence.', actions: ['Review and improve prevention', 'Add detection point'] },
    '9-6': { risk: 'HIGH', guidance: 'Major failure with low-moderate occurrence.', actions: ['Verify control effectiveness'] },
    '9-5': { risk: 'MEDIUM', guidance: 'Major failure but controlled.', actions: ['Maintain prevention controls'] },

    // Medium Severity (6-8) combinations
    '8-10': { risk: 'HIGH', guidance: 'Significant failure with very high occurrence.', actions: ['Process improvement needed', 'Add prevention controls'] },
    '8-8': { risk: 'HIGH', guidance: 'Significant failure with high occurrence.', actions: ['Add error-proofing', 'Improve process capability'] },
    '8-6': { risk: 'MEDIUM', guidance: 'Significant failure with moderate occurrence.', actions: ['Review prevention effectiveness'] },
    '8-4': { risk: 'LOW', guidance: 'Significant failure but low occurrence.', actions: ['Monitor trends'] },

    '7-10': { risk: 'HIGH', guidance: 'Moderate failure with very high occurrence.', actions: ['Focus on occurrence reduction'] },
    '7-7': { risk: 'MEDIUM', guidance: 'Moderate failure with moderate occurrence.', actions: ['Balance prevention and detection'] },
    '7-4': { risk: 'LOW', guidance: 'Moderate failure with low occurrence.', actions: ['Maintain controls'] },

    '6-10': { risk: 'MEDIUM', guidance: 'Minor-moderate failure but very frequent.', actions: ['Reduce occurrence through prevention'] },
    '6-6': { risk: 'MEDIUM', guidance: 'Minor-moderate failure with moderate occurrence.', actions: ['Standard controls adequate'] },
    '6-3': { risk: 'LOW', guidance: 'Minor-moderate failure with low occurrence.', actions: ['Monitor'] },

    // Low Severity (1-5) combinations
    '5-10': { risk: 'MEDIUM', guidance: 'Minor failure but very frequent.', actions: ['Reduce occurrence for efficiency'] },
    '5-5': { risk: 'LOW', guidance: 'Minor failure with moderate occurrence.', actions: ['Standard controls'] },
    '4-10': { risk: 'LOW', guidance: 'Low-impact failure but frequent.', actions: ['Consider occurrence reduction'] },
    '3-10': { risk: 'LOW', guidance: 'Very minor failure.', actions: ['Monitor only'] },
    '2-10': { risk: 'LOW', guidance: 'Negligible impact.', actions: ['No action typically needed'] },
    '1-10': { risk: 'LOW', guidance: 'No effect.', actions: ['Document for completeness'] },
};

// ============================================================
// STATIC COACHING TIPS DATABASE
// Pre-built tips for common situations - NO LLM REQUIRED
// ============================================================

const FIELD_COACHING_TIPS: Record<string, CoachingTip[]> = {
    // Failure Mode field tips
    failureMode: [
        {
            id: 'fm-format',
            category: 'coaching',
            severity: 'info',
            title: 'Failure Mode Format',
            message: 'Use noun-verb format: "[What] [How it fails]". Example: "Hole diameter undersized" or "Weld bead incomplete".',
            aiagVdaRef: 'AIAG-VDA 2.5.1',
        },
        {
            id: 'fm-specific',
            category: 'best-practice',
            severity: 'info',
            title: 'Be Specific',
            message: 'Avoid vague terms like "wrong" or "bad". Describe the specific physical condition.',
        },
    ],

    // Failure Effect fields
    failureEffectLocal: [
        {
            id: 'fe-local',
            category: 'coaching',
            severity: 'info',
            title: 'Local Effect',
            message: 'Describe what happens at THIS process step. Example: "Part cannot be assembled" or "Rework required".',
            aiagVdaRef: 'AIAG-VDA 2.5.2',
        },
    ],
    failureEffectNext: [
        {
            id: 'fe-next',
            category: 'coaching',
            severity: 'info',
            title: 'Next Level Effect',
            message: 'Describe what happens at the NEXT process step or sub-assembly. Example: "Assembly line stoppage".',
            aiagVdaRef: 'AIAG-VDA 2.5.2',
        },
    ],
    failureEffectEnd: [
        {
            id: 'fe-end',
            category: 'coaching',
            severity: 'info',
            title: 'End User Effect',
            message: 'Describe impact to the CUSTOMER. This drives Severity rating. Example: "Vehicle inoperative" or "Safety hazard".',
            aiagVdaRef: 'AIAG-VDA 2.5.2',
        },
    ],

    // Failure Cause field
    failureCause: [
        {
            id: 'fc-specific',
            category: 'coaching',
            severity: 'info',
            title: 'Root Cause Required',
            message: 'Identify the specific ROOT cause, not symptoms. Ask "Why?" until you reach an actionable cause.',
            aiagVdaRef: 'AIAG-VDA 2.5.3',
        },
        {
            id: 'fc-4m',
            category: 'best-practice',
            severity: 'info',
            title: 'Use 4M Categories',
            message: 'Classify causes by: Man (operator), Machine (equipment), Material (inputs), Method (process). Helps identify systemic issues.',
        },
    ],

    // Severity rating
    severity: [
        {
            id: 'sev-basis',
            category: 'aiag-vda',
            severity: 'info',
            title: 'Severity = End User Effect',
            message: 'Rate based on the END USER effect, not local impact. Safety/regulatory = 9-10, major function loss = 7-8.',
            aiagVdaRef: 'AIAG-VDA 2.6.1',
        },
    ],

    // Occurrence rating
    occurrence: [
        {
            id: 'occ-basis',
            category: 'aiag-vda',
            severity: 'info',
            title: 'Occurrence = Prevention Effectiveness',
            message: 'Rate based on how well PREVENTION controls stop the cause. Strong error-proofing = 1-2, no prevention = 9-10.',
            aiagVdaRef: 'AIAG-VDA 2.6.2',
        },
    ],

    // Detection rating
    detection: [
        {
            id: 'det-basis',
            category: 'aiag-vda',
            severity: 'info',
            title: 'Detection = Finding Before Ship',
            message: 'Rate likelihood of detecting failure BEFORE reaching customer. Automated = 1-2, no detection = 10.',
            aiagVdaRef: 'AIAG-VDA 2.6.3',
        },
    ],

    // Prevention controls
    preventionControls: [
        {
            id: 'prev-examples',
            category: 'coaching',
            severity: 'info',
            title: 'Prevention Control Examples',
            message: 'Error-proofing (Poka-Yoke), process parameters, work instructions, training, tool design, material specs.',
        },
    ],

    // Detection controls
    detectionControls: [
        {
            id: 'det-examples',
            category: 'coaching',
            severity: 'info',
            title: 'Detection Control Examples',
            message: 'Visual inspection, gauging, automated sensors, SPC, end-of-line test, functional verification.',
        },
    ],
};

// ============================================================
// VALIDATION RULE TYPES
// ============================================================
type ValidationRule = (item: PfmeaItem, prefs: UserPreferences) => CoachingTip | null;

// ============================================================
// ERROR VALIDATION RULES
// ============================================================
const errorRules: ValidationRule[] = [
    // Missing failure mode
    (item) => (!item.failureMode || item.failureMode.trim() === '') ? {
        id: 'val-no-fm', category: 'validation', severity: 'error',
        title: 'Missing Failure Mode', message: 'Failure mode is required. Describe how the process could fail.',
        field: 'failureMode',
    } : null,

    // Missing end user effect  
    (item) => (!item.failureEffectEnd || item.failureEffectEnd.trim() === '') ? {
        id: 'val-no-effect', category: 'validation', severity: 'error',
        title: 'Missing End User Effect', message: 'End user effect is required to determine Severity rating.',
        field: 'failureEffectEnd',
    } : null,

    // Missing failure cause
    (item) => (!item.failureCause || item.failureCause.trim() === '') ? {
        id: 'val-no-cause', category: 'validation', severity: 'error',
        title: 'Missing Failure Cause', message: 'Root cause is required to determine prevention approach.',
        field: 'failureCause',
    } : null,

    // Invalid severity
    (item) => (item.severity < 1 || item.severity > 10) ? {
        id: 'val-invalid-sev', category: 'validation', severity: 'error',
        title: 'Invalid Severity Rating', message: 'Severity must be between 1 and 10.',
        field: 'severity',
    } : null,

    // Invalid occurrence
    (item) => (item.occurrence < 1 || item.occurrence > 10) ? {
        id: 'val-invalid-occ', category: 'validation', severity: 'error',
        title: 'Invalid Occurrence Rating', message: 'Occurrence must be between 1 and 10.',
        field: 'occurrence',
    } : null,

    // Invalid detection
    (item) => (item.detection < 1 || item.detection > 10) ? {
        id: 'val-invalid-det', category: 'validation', severity: 'error',
        title: 'Invalid Detection Rating', message: 'Detection must be between 1 and 10.',
        field: 'detection',
    } : null,
];

// ============================================================
// WARNING VALIDATION RULES
// ============================================================
const warningRules: ValidationRule[] = [
    // Low occurrence without prevention
    (item) => (item.preventionControls.length === 0 && item.occurrence <= 3) ? {
        id: 'val-low-occ-no-prev', category: 'pre-submission', severity: 'warning',
        title: 'Low Occurrence Without Prevention',
        message: 'Occurrence rating of 3 or less requires documented prevention controls.',
        field: 'preventionControls', aiagVdaRef: 'AIAG-VDA 2.6.2',
    } : null,

    // Low detection without control
    (item) => (item.detectionControls.length === 0 && item.detection <= 3) ? {
        id: 'val-low-det-no-control', category: 'pre-submission', severity: 'warning',
        title: 'Low Detection Without Control',
        message: 'Detection rating of 3 or less requires automated or verified detection method.',
        field: 'detectionControls', aiagVdaRef: 'AIAG-VDA 2.6.3',
    } : null,

    // High severity without actions
    (item) => (item.severity >= 9 && item.recommendedActions.length === 0) ? {
        id: 'val-high-sev-no-action', category: 'pre-submission', severity: 'warning',
        title: 'Safety Critical - No Actions',
        message: 'Severity 9-10 items should have recommended actions regardless of other ratings.',
        field: 'recommendedActions', aiagVdaRef: 'AIAG-VDA 2.6.7',
    } : null,

    // High AP without actions
    (item) => (item.actionPriority === 'H' && item.recommendedActions.length === 0) ? {
        id: 'val-high-ap-no-action', category: 'pre-submission', severity: 'warning',
        title: 'High Priority - No Actions',
        message: 'Action Priority = H requires recommended actions with owner and target date.',
        field: 'recommendedActions',
    } : null,

    // Incomplete effect chain (non-expert)
    (item, prefs) => (prefs.experienceLevel !== 'expert' && !item.failureEffectLocal && !item.failureEffectNext) ? {
        id: 'val-incomplete-chain', category: 'pre-submission', severity: 'warning',
        title: 'Incomplete Effect Chain',
        message: 'Best practice: Complete Local → Next → End User effect chain for accurate Severity.',
        field: 'failureEffectLocal',
    } : null,
];

// ============================================================
// SUGGESTION RULES
// ============================================================
const suggestionRules: ValidationRule[] = [
    // Special characteristic candidate
    (item) => (!item.specialCharacteristic || item.specialCharacteristic === 'none') && item.severity >= 8 ? {
        id: 'sug-cc-candidate', category: 'best-practice', severity: 'info',
        title: 'Special Characteristic Candidate',
        message: 'High severity items often warrant CC (Critical) or SC (Significant) classification.',
        field: 'specialCharacteristic',
    } : null,

    // Link to control plan
    (item, prefs) => (!item.linkedControlPlanId && prefs.experienceLevel !== 'expert') ? {
        id: 'sug-link-cp', category: 'best-practice', severity: 'info',
        title: 'Link to Control Plan',
        message: 'Linking to Control Plan ensures traceability for PPAP.',
        field: 'linkedControlPlanId',
    } : null,
];

// ============================================================
// HELPER: Apply rules and collect non-null results
// ============================================================
function applyRules(rules: ValidationRule[], item: PfmeaItem, prefs: UserPreferences): CoachingTip[] {
    return rules.reduce<CoachingTip[]>((acc, rule) => {
        const result = rule(item, prefs);
        if (result) acc.push(result);
        return acc;
    }, []);
}

// ============================================================
// PRE-SUBMISSION VALIDATION
// Static rules - NO LLM REQUIRED
// ============================================================
export function validatePfmeaItem(item: PfmeaItem, preferences: UserPreferences = DEFAULT_PREFERENCES): ValidationResult {
    const errors = applyRules(errorRules, item, preferences);
    const warnings = applyRules(warningRules, item, preferences);
    const suggestions = preferences.showBestPractices
        ? applyRules(suggestionRules, item, preferences)
        : [];

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        suggestions,
    };
}


// ============================================================
// S×O GUIDANCE HELPER
// Returns static guidance based on Severity × Occurrence
// ============================================================

export function getSxOGuidance(severity: number, occurrence: number): CoachingTip | null {
    const key = `${severity}-${occurrence}`;
    const guidance = SxO_GUIDANCE[key];

    if (guidance) {
        return {
            id: `sxo-${key}`,
            category: 'sxo-guidance',
            severity: guidance.risk === 'CRITICAL' ? 'error' :
                guidance.risk === 'HIGH' ? 'warning' : 'info',
            title: `Risk Level: ${guidance.risk}`,
            message: guidance.guidance,
            aiagVdaRef: 'AIAG-VDA AP Logic Table',
        };
    }

    // Generate generic guidance for unlisted combinations
    const riskLevel = severity * occurrence;
    if (riskLevel >= 64) {
        return {
            id: `sxo-${key}`,
            category: 'sxo-guidance',
            severity: 'error',
            title: 'Risk Level: HIGH',
            message: 'This S×O combination indicates high risk. Focus on prevention controls to reduce occurrence.',
        };
    } else if (riskLevel >= 36) {
        return {
            id: `sxo-${key}`,
            category: 'sxo-guidance',
            severity: 'warning',
            title: 'Risk Level: MEDIUM',
            message: 'Moderate risk. Ensure both prevention and detection controls are adequate.',
        };
    }

    return null;
}

// ============================================================
// GET CONTEXTUAL COACHING TIPS
// Returns tips for a specific field based on current data
// ============================================================

export function getFieldCoachingTips(
    field: string,
    item: Partial<PfmeaItem>,
    preferences: UserPreferences = DEFAULT_PREFERENCES
): CoachingTip[] {
    const tips: CoachingTip[] = [];

    // Get static tips for this field
    const staticTips = FIELD_COACHING_TIPS[field] || [];

    // Filter based on experience level
    if (preferences.experienceLevel === 'beginner') {
        tips.push(...staticTips);
    } else if (preferences.experienceLevel === 'intermediate') {
        // Only show best practices for intermediate
        tips.push(...staticTips.filter(t => t.category === 'best-practice' || t.category === 'aiag-vda'));
    }
    // Experts get minimal tips - only on request

    // Add S×O guidance when rating fields change
    if ((field === 'severity' || field === 'occurrence') && item.severity && item.occurrence) {
        const sxoTip = getSxOGuidance(item.severity, item.occurrence);
        if (sxoTip) {
            tips.push(sxoTip);
        }
    }

    return tips;
}

// ============================================================
// GET RECOMMENDED ACTIONS FOR S×O
// Static suggestions based on S×O combination
// ============================================================

export function getSxOActions(severity: number, occurrence: number): string[] {
    const key = `${severity}-${occurrence}`;
    const guidance = SxO_GUIDANCE[key];

    if (guidance) {
        return guidance.actions;
    }

    // Generic actions for unlisted combinations
    if (severity >= 9) {
        return ['Review prevention controls', 'Consider error-proofing', 'Add verification step'];
    } else if (occurrence >= 8) {
        return ['Improve process capability', 'Add prevention at source', 'Review work instructions'];
    }

    return ['Maintain current controls', 'Monitor effectiveness'];
}

// ============================================================
// EXPERIENCE LEVEL HELPERS
// ============================================================

export function getUserPreferences(): UserPreferences {
    const stored = localStorage.getItem('pfmea-user-preferences');
    if (stored) {
        try {
            return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
        } catch {
            return DEFAULT_PREFERENCES;
        }
    }
    return DEFAULT_PREFERENCES;
}

export function saveUserPreferences(prefs: Partial<UserPreferences>): void {
    const current = getUserPreferences();
    const updated = { ...current, ...prefs };
    localStorage.setItem('pfmea-user-preferences', JSON.stringify(updated));
}

export function setExperienceLevel(level: UserExperienceLevel): void {
    saveUserPreferences({ experienceLevel: level });
}

export default {
    validatePfmeaItem,
    getFieldCoachingTips,
    getSxOGuidance,
    getSxOActions,
    getUserPreferences,
    saveUserPreferences,
    setExperienceLevel,
};
